// @angular/core
import { Component, Input, OnInit, untracked, ChangeDetectionStrategy } from "@angular/core";
import { MaterialDialogService } from "../../../modules/material-module/material-dialog/material-dialog.service";
// Translate
import { TranslateService, TranslateModule } from "@ngx-translate/core";
// Own services
import { ToastService } from "../../../services/shared/ToastService.service";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { ContactService } from "../../../services/shared/ContactService.service";
// Variables
import { LAST_PATCH_NOTES_ARSON } from "../../../LAST_PATCH_NOTES_ARSON";
import { LAST_PATCH_NOTES_USER } from "../../../LAST_PATCH_NOTES_USER";
import { CUSTOM_URLS } from "../../../screens/login/CUSTOM_URLS";
import { PROFILES } from "../../../../assets/profiles/profiles";
import { LANGUAGE } from "../../../services/language/LanguageController.service";
// Components
import { FooterbarDialogComponent } from "./footerbar-dialog/footerbar-dialog.component";
import { NgClass, DatePipe } from "@angular/common";

/**
 * Footer showing app/module versions, a (customizable) brand link, patch notes dialog, contact modal and forced reload.
 * Patch notes depend on the profile (ARSON or user, by language); inputs: `sessionProfile`, `appVersion`, `subscriberFooter`, `sidebarLocked`.
 */
@Component({
  selector: "app-footerbar",
  templateUrl: "./footerbar.component.html",
  styleUrls: ["./footerbar.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgClass, DatePipe, TranslateModule]
})
export class FooterbarComponent implements OnInit {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  @Input() subscriberFooter: boolean;
  @Input() sidebarLocked: boolean;
  @Input() sessionProfile: string;
  readonly PROFILES = PROFILES;
  @Input() appVersion: { module: string; version: string }[];
  frontVersion: string;
  versionTitle: string = "";
  latestPatchInfo: {
    date: string;
    info: { title: string; actions: string[] }[];
    profile: string;
  }[];
  date: number;
  customFooter = CUSTOM_URLS.find(url => window.location.href.includes(url.urlText))?.footer;
  footerLink: string = this.customFooter?.footerLink
    ? this.customFooter.footerLink
    : "http://arsonmetering.com/";
  footerText: string = this.customFooter?.footerText ? this.customFooter.footerText : "Arson";

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly ContactService: ContactService,
    private readonly MaterialDialogService: MaterialDialogService,
    private readonly SessionDataService: SessionDataService,
    private readonly ToastService: ToastService,
    private readonly translate: TranslateService
  ) {}

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    this.date = Date.now();
    this.frontVersion = this.appVersion[0].version;
    this.appVersion?.forEach((appModule: { module: string; version: string }) => {
      this.versionTitle += appModule.module + ": " + appModule.version + "\n";
    });
    this.formatLastPatchNotes();
  }

  /***************************************************************************/
  // ANCHOR Methods
  /***************************************************************************/

  showLastPathNotes(): void {
    this.MaterialDialogService.openDialog(FooterbarDialogComponent, {
      latestPatchInfo: this.latestPatchInfo,
      appVersion: this.appVersion
    });
  }

  forceUpdate(): void {
    document.location.reload();
  }

  formatLastPatchNotes(): void {
    let latestPatchInfo =
      this.sessionProfile === PROFILES.ARSON
        ? LAST_PATCH_NOTES_ARSON
        : LAST_PATCH_NOTES_USER[untracked(() => this.SessionDataService.language())]
          ? LAST_PATCH_NOTES_USER[untracked(() => this.SessionDataService.language())]
          : LAST_PATCH_NOTES_USER[LANGUAGE.INGLES];
    if (latestPatchInfo) {
      this.latestPatchInfo = [];
      let lastPatchUpdates = latestPatchInfo.split("###");
      lastPatchUpdates.forEach((updateInfo: string) => {
        let patchDate = updateInfo.split("\n")[0];
        if (patchDate && patchDate !== "") {
          let latestPatchInfoArray = updateInfo.split("*");
          latestPatchInfoArray.sort((a, b) => a.localeCompare(b));
          let latestPatchInfoData: { title: string; actions: string[] }[] = [];
          latestPatchInfoArray.forEach((note: string) => {
            let noteTitle = note.substring(0, note.indexOf(":") + 1);
            let noteActions = note.replace(noteTitle, "");
            let noteActionsArray = noteActions
              .split("-")
              .filter((action: string) => /[a-z]/i.test(action));
            if (noteTitle && noteActions) {
              latestPatchInfoData.push({
                title: noteTitle,
                actions: noteActionsArray
              });
            }
          });
          this.latestPatchInfo.push({
            date: patchDate,
            info: latestPatchInfoData,
            profile: this.sessionProfile
          });
        }
      });
    }
  }

  showContact(): void {
    this.ContactService.showContact(this.customFooter);
  }
}
